import { forkJoin as observableForkJoin, Observable } from 'rxjs';
import { Component, OnInit, AfterViewInit, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { LoadingPanelService, LoadingPanelSettings } from '../shared/loading-panel/loading-panel.service';
import { GenericModalService, ModalSettingsInput, ModalMultiButton, ModalSettingsMessage } from '../shared/generic-modal/generic-modal.service';
import { Clone } from '../shared/utils/clone';
import { ToastService, ToastType } from '../shared/toast/toast.service';

import { Partner, PartnerLoadOptions, PartnerSearch, PartnerIdentificationType, IdentificationType, PartnerSearchSettings } from './models';
import { PartnerService } from './services/partner.service';
import { PartnerComponentBase } from './partner-search-details.base';

import { BizDataGridDirective } from '../shared/grid/biz-grid.directive';
import { DxDataGridModule } from 'devextreme-angular';
import { DxoScrollingModule, DxoSelectionModule, DxiColumnModule } from 'devextreme-angular/ui/nested';
import { NgClass } from '@angular/common';
import { PermissionRequiredDirective } from '../shared/authentication/permission-required.directive';

@Component({
    templateUrl: './partner-search-details-proxy-partners.component.html',
    selector: 'ep-partner-details-proxy-partners',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DxDataGridModule, BizDataGridDirective, DxoScrollingModule, DxoSelectionModule, DxiColumnModule, NgClass, PermissionRequiredDirective]
})

export class PartnerSearchDetailsProxyPartnersComponent extends PartnerComponentBase {
    private loadingPanelService = inject(LoadingPanelService);
    private toastService = inject(ToastService);
    private partnerService = inject(PartnerService);

    public isEditing = false;
    public allowEdit = false;
    public partnerConnections: Partner[] = [];
    public originalPartnerConnections: Partner[] = [];

    public availablePartners: PartnerSearch[] = [];
    public availablePartnersSelected: PartnerSearch[] = [];
    public selectedPartnersSelected: Partner[] = [];

    @ViewChild('gridAvailablePartners', { static: true }) gridAvailablePartners: BizDataGridDirective;
    @ViewChild('gridSelectedPartners', { static: true }) gridSelectedPartners: BizDataGridDirective;

    async partnerSet(): Promise<void> {
        const partnerId = this.selectedPartner?.PartnersId;
        const partnerConnections = partnerId
            ? await this.partnerService.getProxyPartnerConnections(partnerId)
            : [];
        this.partnerConnections = partnerConnections.sort((p1, p2) => p1.PartnerName.localeCompare(p2.PartnerName));
        this.availablePartners = [];
        this.allowEdit = this.selectedPartner?.IsProxyPartner === true;
    }

    edit() {
        let invoker = () => {
            this.originalPartnerConnections = [];
            Clone.extend(true, this.originalPartnerConnections, this.partnerConnections);
            this.isEditing = true;
            this.setEditMode(true);
        };

        if (!this.availablePartners || this.availablePartners.length == 0) { // Load all available partners the first time the user hits the edit button for the selected partner
            this.loadingPanelService.showLoadingPanelDirect('Loading available partners...');
            const searchSettings = new PartnerSearchSettings();
            searchSettings.skipMaxSearchResult = true; // Make sure we show all available partners
            this.partnerService.searchPartners(searchSettings).then(result => {
                this.availablePartners = result.Partners.filter(p => !p.IsProxyPartner).sort((p1, p2) => p1.PartnerName.localeCompare(p2.PartnerName))
                invoker();
                this.loadingPanelService.hideLoadingPanel();
            });
        }
        else {
            invoker();
        }
    }

    cancel() {
        this.partnerConnections = this.originalPartnerConnections;
        this.isEditing = false;
        this.setEditMode(false);
    }

    save() {
        this.loadingPanelService.showLoadingPanelDirect('Saving...');

        let connectedPartnerIds = this.partnerConnections.map(p => p.PartnersId);
        this.partnerService.saveProxyPartnerConnections(this.selectedPartner.PartnersId, connectedPartnerIds).then(() => {
            this.isEditing = false;
            this.setEditMode(false);
            this.loadingPanelService.hideLoadingPanel();
            this.toastService.showToast('Proxy partners saved...', ToastType.success);
        }).catch(this.loadingPanelService.hideLoadingPanel);
    }

    //
    // PARTNERS
    //
    addSelectedPartners() {
        this.availablePartnersSelected.forEach(p => {
            if (!this.partnerConnections.some(sp => sp.PartnersId == p.PartnerId)) {

                // Create a new 'real' partner because that's whats needed by the data-group
                let partner = new Partner();
                partner.PartnersId = p.PartnerId;
                partner.PartnerName = p.PartnerName;
                partner.MainIdentificationType = new PartnerIdentificationType();
                partner.MainIdentificationType.Value = p.MainIdentificationValue;
                partner.MainIdentificationType.IdentificationType = new IdentificationType();
                partner.MainIdentificationType.IdentificationType.Name = p.MainIdentificationName;

                this.partnerConnections.push(partner);
            }
        });
        this.clearPartnerSelection();
    }

    removeSelectedPartners() {
        this.selectedPartnersSelected.forEach(p => {
            this.partnerConnections.splice(this.partnerConnections.indexOf(p), 1);
        });
        this.clearPartnerSelection();
    }

    selectionChangedAvailablePartners(e: any) {
        this.availablePartnersSelected = e.selectedRowsData;
    }

    selectionChangedSelectedPartners(e: any) {
        this.selectedPartnersSelected = e.selectedRowsData;
    }

    clearPartnerSelection() {
        this.gridAvailablePartners.instance.clearSelection();
        this.gridSelectedPartners.instance.clearSelection();
    }
}
